import router from '@/router';
import axios from 'axios';
import { clearPendingLogin } from './pendingLogin';

/**
 * Tab-scoped marker that the browser holds an authenticated httpOnly session
 * cookie (`yango-session`). The UUID itself is never stored in JS-accessible
 * storage (Q-17 / AB#3390 W7).
 */
export const SESSION_ACTIVE_KEY = 'yango-session-active';

/**
 * Tab-scoped storage for the legacy {@code session-id} header fallback (AD-Q17).
 * Holds the opaque UUID returned by {@code POST /api/verifyOtp} when the
 * httpOnly cookie cannot be sent on cross-origin XHR.
 */
export const SESSION_HEADER_KEY = 'yango-session-header-id';

/**
 * Endpoints that must not receive the tab-scoped {@code session-id} header and
 * whose 401/403 responses must not trigger session logout.
 *
 * Includes auth flows, integration surfaces ({@code /invoice/**}), and
 * internal ops monitors ({@code /output}, {@code /logs/**}) that do not use
 * the backoffice session UUID contract.
 */
export const PUBLIC_AUTH_PATH_FRAGMENTS = [
  'api/login',
  'api/verifyOtp',
  'api/refreshOTP',
  'api/logout',
  'requestPasswordReset',
  'verifyResetOtp',
  'resetPassword',
  '/invoice',
  '/output',
  '/exceptions',
  '/logs',
  '/app/info',
] as const;

/**
 * Returns true when the URL targets a public endpoint that must not receive
 * the legacy {@code session-id} header from the axios request interceptor.
 *
 * @param url - Request URL (absolute or relative).
 */
export function isPublicAuthUrl(url: string): boolean {
  return PUBLIC_AUTH_PATH_FRAGMENTS.some((fragment) => url.includes(fragment));
}

/**
 * Returns true when the URL targets the SynergyApi usage-tracking batch
 * endpoint. These requests must not receive the legacy {@code session-id}
 * header from the axios request interceptor.
 *
 * @param url - Request URL (absolute or relative).
 */
export function isUsageTrackingUrl(url: string): boolean {
  return url.includes('SynergyApi/api/v1/usage') || url.includes('/usage/record/batch');
}

let expiryRedirectInFlight = false;

/**
 * Returns whether the client considers the user authenticated.
 *
 * Under the cookie model this checks only the tab-scoped active flag; the
 * opaque session UUID lives in an httpOnly cookie the SPA cannot read.
 */
export function hasStoredSession(): boolean {
  try {
    return sessionStorage.getItem(SESSION_ACTIVE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Marks the tab as authenticated after successful OTP verification.
 */
export function setSessionActive(): void {
  sessionStorage.setItem(SESSION_ACTIVE_KEY, 'true');
}

/**
 * Establishes an authenticated tab session after OTP verification.
 *
 * Sets both the tab-scoped active flag and the {@code session-id} header fallback
 * UUID returned by {@code POST /api/verifyOtp}.
 *
 * @param sessionId - Opaque session UUID from {@code verifyOtp} response body.
 */
export function establishSession(sessionId: string): void {
  setSessionHeaderId(sessionId);
  setSessionActive();
}

/**
 * Stores the session UUID for the axios {@code session-id} header fallback.
 *
 * @param sessionId - Opaque session UUID from {@code verifyOtp} response body.
 */
export function setSessionHeaderId(sessionId: string): void {
  try {
    sessionStorage.setItem(SESSION_HEADER_KEY, sessionId);
  } catch {
    // ignore storage failures in restricted environments
  }
}

/**
 * Returns the tab-scoped session UUID for the {@code session-id} header, if set.
 */
export function getSessionHeaderId(): string | null {
  try {
    return sessionStorage.getItem(SESSION_HEADER_KEY);
  } catch {
    return null;
  }
}

/**
 * Clears authenticated session state and best-effort clears the httpOnly cookie
 * via {@code POST /api/logout}.
 */
export function clearSession(): void {
  axios.post(window.yangoServer + 'api/logout').catch(() => undefined);
  try {
    sessionStorage.removeItem(SESSION_ACTIVE_KEY);
    sessionStorage.removeItem(SESSION_HEADER_KEY);
  } catch {
    // ignore storage failures in restricted environments
  }
  localStorage.removeItem('session-id');
  localStorage.removeItem('auth');
  clearPendingLogin();
}

/**
 * Extracts a human-readable message from Yango API error bodies.
 *
 * @param data - Raw response body from the backend.
 */
export function extractApiErrorMessage(data: unknown): string {
  if (typeof data === 'string') {
    return data;
  }
  if (data && typeof data === 'object') {
    const shape = data as Record<string, unknown>;
    if (typeof shape.message === 'string') {
      return shape.message;
    }
    if (typeof shape.Message === 'string') {
      return shape.Message;
    }
    if (typeof shape.error === 'string') {
      return shape.error;
    }
  }
  return '';
}

/**
 * Detects backend session invalidation responses while the client still holds a session id.
 *
 * @param error - Axios error object from a failed API call.
 */
export function isSessionExpiredError(error: unknown): boolean {
  if (!hasStoredSession()) {
    return false;
  }
  const axiosError = error as {
    response?: { status?: number; config?: { url?: string }; data?: unknown };
  };
  const status = axiosError.response?.status;
  if (status !== 401 && status !== 403) {
    return false;
  }
  const url = axiosError.response?.config?.url ?? '';
  if (isPublicAuthUrl(url)) {
    return false;
  }
  const message = extractApiErrorMessage(axiosError.response?.data);
  return /session/i.test(message)
    || /expired/i.test(message)
    || /please login/i.test(message);
}

/**
 * Clears session state and returns the user to the login screen once per expiry burst.
 */
export function handleSessionExpiredResponse(): void {
  if (expiryRedirectInFlight) {
    return;
  }
  expiryRedirectInFlight = true;
  clearSession();

  const goLogin = (): void => {
    if (router.currentRoute.name === 'Login') {
      expiryRedirectInFlight = false;
      return;
    }
    router.push({ name: 'Login' }).finally(() => {
      expiryRedirectInFlight = false;
    });
  };

  if (window.Toast) {
    window.Toast.fire({
      icon: 'warning',
      title: 'Session expired',
      text: 'Please log in again.',
    }).finally(goLogin);
  } else {
    goLogin();
  }
}

/**
 * Marker set on axios errors handled by the global session interceptor.
 */
export const SESSION_EXPIRED_HANDLED = 'sessionExpiredHandled';

/**
 * Returns true when the global session interceptor already handled this error.
 *
 * @param error - Axios-like error object.
 */
export function wasSessionExpiryHandled(error: unknown): boolean {
  return Boolean(
    error
    && typeof error === 'object'
    && (error as Record<string, unknown>)[SESSION_EXPIRED_HANDLED] === true,
  );
}